// FILE: src/pages/admin/staff/AdminStaffInvitePage.jsx
// Invite a staff member (RBAC; needs staff.manage): name, email, role and,
// for instructors, the DDI registration number (needed to answer
// course-content questions; BDE Policy Manual 3.4.2). We email them the
// usual set-up link: they choose a password and five security questions,
// with no licence, enrolment or course. Every failure is shown in words
// next to its field and in an alert. When the account was made but the
// email failed (502 INVITE_EMAIL_FAILED { user }) the page says so and
// offers to send it again.
//   POST /api/admin/staff { email, firstName, lastName, middleName?, phone?, staffRoleId, staffRegistration? }
import React, { useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Alert, AlertTitle, Box, Button, Card, CardContent, Stack, TextField, Typography } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import fetchWithAuth from "../../../utils/fetchWithAuth";
import { PageHeader, Section } from "../../../components/ui";
import StaffRolePicker from "../../../components/admin/StaffRolePicker";
import { errorPayload } from "../../../components/admin/apiError";
import { rbacErrorText } from "../../../components/admin/staffBits";
import { errorCode, errorMessage, messageFor } from "../../../i18n/errors";
import { STAFF_REGISTRATION_MAX, inviteStaff, useRoles } from "../../../services/roles";

const EMPTY = { firstName: "", middleName: "", lastName: "", email: "", phone: "", staffRoleId: "", staffRegistration: "" };
const ORDER = ["firstName", "lastName", "email", "staffRoleId", "staffRegistration"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const BREADCRUMBS = [{ label: "Staff", to: "/admin/staff" }, { label: "Invite staff member" }];

export function validateStaffInvite(form) {
  const errors = {};
  if (!form.firstName.trim()) errors.firstName = "Enter the staff member's first name.";
  if (!form.lastName.trim()) errors.lastName = "Enter the staff member's last name.";
  const email = form.email.trim();
  if (!email) errors.email = "Enter the staff member's email address.";
  else if (!EMAIL_RE.test(email)) errors.email = messageFor("INVALID_EMAIL");
  if (!form.staffRoleId) errors.staffRoleId = "Choose a role.";
  if (form.staffRegistration.trim().length > STAFF_REGISTRATION_MAX) {
    errors.staffRegistration = `Use ${STAFF_REGISTRATION_MAX} characters or fewer.`;
  }
  return errors;
}

/** Field errors from a server refusal. */
function fieldErrors(err) {
  const code = errorCode(err);
  if (["EMAIL_IN_USE", "INVALID_EMAIL"].includes(code)) return { email: errorMessage(err) };
  const out = {};
  for (const p of errorPayload(err).problems || []) {
    if (p.field === "staffRoleId") out.staffRoleId = "That role no longer exists. Choose another one.";
    else if (p.field === "staffRegistration") out.staffRegistration = `Use ${STAFF_REGISTRATION_MAX} characters or fewer.`;
    else if (ORDER.includes(p.field)) out[p.field] = "Check this field.";
  }
  return out;
}

export default function AdminStaffInvitePage() {
  const navigate = useNavigate();
  const roles = useRoles();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [emailFailed, setEmailFailed] = useState(null); // { user, email }
  const [resend, setResend] = useState({ busy: false, error: null });
  const refs = useRef({});
  const alertRef = useRef(null);
  const register = (name) => (el) => {
    refs.current[name] = el;
  };

  const change = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };
  const onField = (e) => change(e.target.name, e.target.value);

  const done = (email) => navigate("/admin/staff", { state: { notice: `Invitation sent to ${email}. They'll appear as “Invitation sent” until they set up their account.` } });

  const submit = async (e) => {
    e.preventDefault();
    setServerError(null);
    const found = validateStaffInvite(form);
    setErrors(found);
    if (Object.keys(found).length) {
      refs.current[ORDER.find((f) => found[f])]?.focus();
      return;
    }
    const body = {
      firstName: form.firstName.trim(),
      middleName: form.middleName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      staffRoleId: form.staffRoleId,
      staffRegistration: form.staffRegistration.trim()
    };
    setSubmitting(true);
    try {
      await inviteStaff(body);
      done(body.email);
    } catch (err) {
      if (errorCode(err) === "INVITE_EMAIL_FAILED") {
        setEmailFailed({ user: errorPayload(err).user || null, email: body.email });
        return;
      }
      setServerError(err || "INTERNAL_ERROR");
      const fields = fieldErrors(err);
      setErrors((prev) => ({ ...prev, ...fields }));
      const first = ORDER.find((f) => fields[f]);
      setTimeout(() => (first ? refs.current[first]?.focus() : alertRef.current?.focus()), 0);
    } finally {
      setSubmitting(false);
    }
  };

  const sendAgain = async () => {
    setResend({ busy: true, error: null });
    try {
      await fetchWithAuth(`/api/admin/users/${emailFailed.user._id}/resend-invite`, { method: "POST" });
      done(emailFailed.email);
    } catch (err) {
      setResend({ busy: false, error: err || "INTERNAL_ERROR" });
    }
  };

  if (emailFailed) {
    return (
      <Box>
        <PageHeader title="Invite staff member" breadcrumbs={BREADCRUMBS} />
        <Card sx={{ maxWidth: 720, bgcolor: "zun.warningTint", borderColor: "transparent" }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1.5 }}>
              <WarningAmberOutlinedIcon aria-hidden sx={{ color: "warning.main", fontSize: 32 }} />
              <Typography variant="h3" component="h2" sx={{ overflowWrap: "anywhere" }}>
                Account created, but the invitation email couldn't be sent
              </Typography>
            </Stack>
            <Typography sx={{ mb: 3, overflowWrap: "anywhere" }}>
              The staff account for {emailFailed.email} exists, so don't invite them again. Send the invitation again now, or
              later from their edit page.
            </Typography>
            {resend.error && (
              <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                {errorMessage(resend.error)}
              </Alert>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              {emailFailed.user?._id && (
                <Button variant="contained" startIcon={<SendOutlinedIcon />} onClick={sendAgain} disabled={resend.busy}>
                  {resend.busy ? "Sending the invitation…" : "Send the invitation again"}
                </Button>
              )}
              <Button component={RouterLink} to="/admin/staff" variant="outlined">
                Back to staff
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Invite staff member"
        breadcrumbs={BREADCRUMBS}
        description="We'll email them a link to set up their account: a password and five security questions. Then they sign in with their email, password and a code we email them."
      />
      <Box component="form" noValidate onSubmit={submit} aria-label="Invite staff member" sx={{ maxWidth: 720 }}>
        <Stack spacing={2.5}>
          {serverError && (
            <Alert severity="error" ref={alertRef} tabIndex={-1} role="alert">
              <AlertTitle>We couldn't send the invitation</AlertTitle>
              {rbacErrorText(serverError)}
            </Alert>
          )}
          <Section id="staff-details" title="Who they are">
            <Stack spacing={2.5}>
              <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                <TextField
                  id="firstName"
                  name="firstName"
                  label="First name"
                  value={form.firstName}
                  onChange={onField}
                  inputRef={register("firstName")}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  autoComplete="off"
                  required
                  fullWidth
                />
                <TextField
                  id="middleName"
                  name="middleName"
                  label="Middle name"
                  value={form.middleName}
                  onChange={onField}
                  helperText="Optional"
                  autoComplete="off"
                  fullWidth
                />
              </Box>
              <TextField
                id="lastName"
                name="lastName"
                label="Last name"
                value={form.lastName}
                onChange={onField}
                inputRef={register("lastName")}
                error={!!errors.lastName}
                helperText={errors.lastName}
                autoComplete="off"
                required
                fullWidth
              />
              <TextField
                id="email"
                name="email"
                type="email"
                label="Email"
                value={form.email}
                onChange={onField}
                inputRef={register("email")}
                error={!!errors.email}
                helperText={errors.email || "Their work email. The invitation and sign-in codes go here."}
                autoComplete="off"
                required
                fullWidth
              />
              <TextField id="phone" name="phone" type="tel" label="Phone" value={form.phone} onChange={onField} helperText="Optional" autoComplete="off" fullWidth />
            </Stack>
          </Section>

          <Section
            id="staff-access"
            title="Role and registration"
            description="The role decides what they can see and do in the admin area. You can change it later."
          >
            <Stack spacing={2.5}>
              <StaffRolePicker
                value={form.staffRoleId}
                onChange={(v) => change("staffRoleId", v)}
                roles={roles.roles}
                loading={roles.loading}
                error={roles.error}
                onRetry={roles.reload}
                fieldError={errors.staffRoleId}
                inputRef={register("staffRoleId")}
              />
              <TextField
                id="staffRegistration"
                name="staffRegistration"
                label="DDI registration number (needed to answer course-content questions)"
                value={form.staffRegistration}
                onChange={onField}
                inputRef={register("staffRegistration")}
                error={!!errors.staffRegistration}
                helperText={errors.staffRegistration || `Optional. The Digital Driving Instructor registration, up to ${STAFF_REGISTRATION_MAX} characters.`}
                autoComplete="off"
                slotProps={{ htmlInput: { maxLength: STAFF_REGISTRATION_MAX, spellCheck: false } }}
                fullWidth
              />
            </Stack>
          </Section>

          <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} sx={{ justifyContent: "flex-end" }}>
            <Button component={RouterLink} to="/admin/staff" variant="outlined">
              Cancel
            </Button>
            <Button type="submit" variant="contained" startIcon={<SendOutlinedIcon />} disabled={submitting}>
              {submitting ? "Sending the invitation…" : "Send invitation"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
